/**
 * Lógica pura do formulário «Novo restaurante» (#418) — fora do componente
 * para poder ser testada sem DOM. Validação no submit: nome obrigatório,
 * regras de slug do shared e formato do e-mail do dono (EmailSchema do
 * shared — nenhuma regra inventada aqui).
 */
import { EmailSchema, slugifyName } from '@self-restaurant/shared'
import { slugMessage, suggestSlugLocal, validateSlugField } from '../../lib/tenant-slug.js'

type Translator = (key: string, vars?: Record<string, string>) => string

/** nome → slug derivado; nome vazio → slug vazio (nunca «restaurante»). */
export function slugFromName(name: string): string {
  return name.trim() ? slugifyName(name) : ''
}

/**
 * Divide o texto interpolado no placeholder («Usar {suggestion}» → antes/depois)
 * para isolar o valor num nó próprio (p.ex. <bdi dir="ltr"> em torno do slug).
 * Placeholder ausente → [texto, ''].
 */
export function splitPlaceholder(text: string, placeholder: string): [string, string] {
  const marker = `{${placeholder}}`
  const idx = text.indexOf(marker)
  if (idx === -1) return [text, '']
  return [text.slice(0, idx), text.slice(idx + marker.length)]
}

/**
 * Locale exibido no seletor: '' = usuário ainda não escolheu → cai no default
 * derivado do catálogo. Assim o catálogo chegando async não apaga a escolha
 * já feita (#418 QA) — a escolha explícita sempre vence.
 */
export function selectedLocale(chosen: string, defaultLocale: string): string {
  return chosen || defaultLocale
}

export type CreateTenantFormInput = { name: string; slug: string; ownerEmail: string }

export type CreateTenantFormErrors = {
  /** mensagem para o campo Nome */
  name?: string
  /** erro de slug (com sugestão de 1 clique) */
  slug?: { message: string; suggestion: string | null }
  /** mensagem para o campo E-mail do dono */
  ownerEmail?: string
}

/**
 * Valida os campos na ordem do formulário (nome → slug → e-mail). O chamador
 * foca o primeiro campo com erro; nada é enviado com erro presente.
 */
export function validateCreateTenantForm(
  input: CreateTenantFormInput,
  t: Translator,
): CreateTenantFormErrors {
  const errors: CreateTenantFormErrors = {}
  // mesmo mínimo do servidor (TenantCreateSchema name min(2))
  if (input.name.trim().length < 2) errors.name = t('admin.tenant.name_required')
  const slug = validateSlugField(input.slug)
  if (!slug.ok)
    errors.slug = {
      message: slugMessage(slug.kind, t),
      suggestion: suggestSlugLocal(input.slug),
    }
  if (input.ownerEmail && !EmailSchema.safeParse(input.ownerEmail).success)
    errors.ownerEmail = t('admin.tenant.owner_email_invalid')
  return errors
}
